import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ArrowLeft, List, TrendingUp, Trash2, Pencil, Columns } from 'lucide-react';
import { Button } from './ui/button';
import PassViewGraph from './ViewGraph';
import TiltViewDetailTable from './TiltViewDetailTable';
import { usePassViewDetails, useCurrentProject, useTiltViewDetails } from '../redux/WeldData/hooks/useWeldData';
import { updateWeldNumber } from '../redux/WeldData/actions';
import { UpdateWeldNumberDialog } from '../features/project/components/dashboard/production/UpdateWeldNumberDialog';
import { formatTimeForDisplay, formatDuration, formatTimestamp, formatDateTime } from '../lib/formatUtils';
import { formatTimestampForDisplay, convertWeldDataValue, getParameterUnitSuffix, getUnitFromSettings } from '../utils/unitUtils';
import { formatHeatInputLabel, HEAT_INPUT_DISPLAY_NAME } from '../utils/weldParameterLabels';
import { ColumnSelectorModal, type ColumnDefinition } from './ui/ColumnSelectorModal';
import { createUpdateUserTableLayout, getAllUserTableLayouts } from '../redux/UserTableLayout/actions';

// Define the TiltViewRow interface locally
interface TiltViewRow {
  id: number;
  status: string;
  stationName: string;
  bugType: string;
  torch: string;
  pass: string;
  tiltRange: string;
  // Tilt parameters for API calls
  tilt_start?: number;
  tilt_end?: number;
  weldStartTime: string;
  weldEndTime: number | null;
  weldTime: number | null;
  autoincrement: string;
  welduid_s_event_torch?: string;
  bugNumber?: string;
  bandDiameter?: number | null;
  wireDiameter?: number | null;
  loggingDistance?: number | null;
  pipeDiameter?: number | null;
  current: { min: number | null; avg: number | null; max: number | null };
  volt: { min: number | null; avg: number | null; max: number | null };
  travelSpeed: { min: number | null; avg: number | null; max: number | null };
  trueEnergy: { min: number | null; avg: number | null; max: number | null };
  heat: { min: number | null; avg: number | null; max: number | null };
  wireSpeed: { min: number | null; avg: number | null; max: number | null };
  frequency: { min: number | null; avg: number | null; max: number | null };
  target: { min: number | null; avg: number | null; max: number | null };
  horizontalBias: { min: number | null; avg: number | null; max: number | null };
  totalWireConsumed: { min: number | null; avg: number | null; max: number | null };
  arcImpedance: { min: number | null; avg: number | null; max: number | null };
  oscillationWidth: { min: number | null; avg: number | null; max: number | null };
  passView?: any;
  jobNumber?: string;
  job_number?: string;
}

interface TiltViewDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  tiltData: TiltViewRow;
  weldData?: any; // Add weld data prop to access job_number
  statusCalculationMethod?: string;
  canEdit?: boolean;
}

// Column definitions for TiltViewDetailTable
const TILT_VIEW_DETAIL_COLUMNS: ColumnDefinition[] = [
  // Sticky columns
  { key: 'sl-no', label: 'Sl.no', category: 'Basic Info' },
  { key: 'status', label: 'Status', category: 'Basic Info' },
  { key: 'event', label: 'Event', category: 'Basic Info' },
  { key: 'time', label: 'Time', category: 'Basic Info' },
  // Scrollable columns
  { key: 'tilt', label: 'Tilt', category: 'Basic Info' },
  { key: 'pass', label: 'Pass', category: 'Basic Info' },
  { key: 'pipe', label: 'Pipe', category: 'Basic Info' },
  { key: 'zone', label: 'Zone', category: 'Basic Info' },
  { key: 'distance', label: 'Distance', category: 'Basic Info' },
  // Parameter columns
  { key: 'travel-speed', label: 'Travel Speed', category: 'Parameters' },
  { key: 'voltage', label: 'Voltage', category: 'Parameters' },
  { key: 'current', label: 'Current', category: 'Parameters' },
  { key: 'wire-speed', label: 'Wire Speed', category: 'Parameters' },
  { key: 'oscillation-width', label: 'Oscillation Width', category: 'Parameters' },
  { key: 'target', label: 'Target', category: 'Parameters' },
  { key: 'horizontal-bias', label: 'Horizontal Bias', category: 'Parameters' },
  { key: 'frequency', label: 'Frequency', category: 'Parameters' },
  { key: 'total-wire-consumed', label: 'Total Wire Consumed', category: 'Parameters' },
  { key: 'true-energy', label: 'True Energy', category: 'Parameters' },
  { key: 'heat', label: HEAT_INPUT_DISPLAY_NAME, category: 'Parameters' },
  { key: 'head-angle', label: 'Head Angle', category: 'Parameters' },
  { key: 'arc-impedance', label: 'Arc Impedance', category: 'Parameters' },
];

// Table name for API
const TABLE_NAME = 'tiltViewDetail';

// Helper function to get all column keys
const getAllColumnKeys = (columns: ColumnDefinition[]): string[] => {
  const keys: string[] = [];
  columns.forEach(col => {
    keys.push(col.key);
    if (col.subColumns) {
      col.subColumns.forEach(sub => keys.push(sub.key));
    }
  });
  return keys;  
};

const TiltViewDetailModal: React.FC<TiltViewDetailModalProps> = ({ isOpen, onClose, tiltData, weldData, statusCalculationMethod, canEdit = true }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('table');
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [showAllInfo, setShowAllInfo] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [weldToUpdate, setWeldToUpdate] = useState<any>(null);

  const updateWeldNumberLoading = useSelector((state: any) => state.weldData?.updateWeldNumberLoading || false);

  const { tiltViewDetails, tiltViewDetailsLoading, tiltViewDetailsError, loadTiltViewDetails, clearTiltViewDetailsData } = useTiltViewDetails();
  const { currentProject } = useCurrentProject();

  // Get user table layouts from Redux state
  const userTableLayouts = useSelector((state: any) => state.userTableLayout?.layouts || []);
  const layoutsLoading = useSelector((state: any) => state.userTableLayout?.loading || false);

  // Store layout ID
  const layoutIdRef = useRef<number | null>(null);

  // Store default columns from API (for Reset button in ColumnSelectorModal)
  const [defaultColumnsFromAPI, setDefaultColumnsFromAPI] = useState<string[] | null>(null);

  // Column visibility state - initialize with all columns, will be updated from API
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => 
    getAllColumnKeys(TILT_VIEW_DETAIL_COLUMNS)
  );

  // Column selector modal visibility state
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);

  // Load layouts from API when component mounts/opens
  useEffect(() => {
    if (isOpen) {
      // Saga will get userId from Redux state
      dispatch(getAllUserTableLayouts({ 
        table_name: 'tiltViewDetail'
      }));
    }
  }, [isOpen, dispatch]);

  // Update column state when layouts are loaded from API
  useEffect(() => {
    if (userTableLayouts.length > 0) {
      // Find layout for tilt view detail (saga already filtered by user_id)
      const layout = userTableLayouts.find(
        (layout: any) => layout.table_name === TABLE_NAME
      );

      // Update layout ID, columns, and default columns from API
      if (layout) {
        layoutIdRef.current = layout.user_table_layout_id;
        if (layout.layout?.columns && Array.isArray(layout.layout.columns)) {
          setVisibleColumns(layout.layout.columns);
          // Store as default columns for Reset button
          setDefaultColumnsFromAPI(layout.layout.columns);
        }
      }
    }
  }, [userTableLayouts]);

  // Column visibility handler - save to API
  const handleColumnsSave = useCallback((selectedColumns: string[]) => {
    setVisibleColumns(selectedColumns);
    
    const payload: any = {
      table_name: TABLE_NAME,
      layout: {
        columns: selectedColumns
      }
    };
    
    // If we have an existing layout ID, include it for update
    if (layoutIdRef.current) {
      payload.user_table_layout_id = layoutIdRef.current;
    }
    
    // Saga will add userId from Redux state
    dispatch(createUpdateUserTableLayout(payload));
  }, [dispatch]);

  // Update layout ID when create/update succeeds
  const createUpdateSuccess = useSelector((state: any) => state.userTableLayout?.currentLayout);
  useEffect(() => {
    if (createUpdateSuccess && createUpdateSuccess.table_name === TABLE_NAME) {
      layoutIdRef.current = createUpdateSuccess.user_table_layout_id;
    }
  }, [createUpdateSuccess]);







  // Load tilt view details when modal opens
  useEffect(() => {
    if (isOpen && tiltData) {
      // Use welduid_s_event_torch if available, otherwise use id
      const welduidEventTorch = tiltData.welduid_s_event_torch || tiltData.id?.toString();
      
      // Use the tilt view details hook with tilt-specific parameters
      if (tiltData.tilt_start !== undefined && tiltData.tilt_end !== undefined) {
        // Use the new tilt view details hook with tilt parameters
        loadTiltViewDetails(welduidEventTorch, 'Tilt', 'Lead', tiltData.tilt_start, tiltData.tilt_end);
      } else {
        // Fallback to pass view details if tilt parameters are not available
        loadTiltViewDetails(welduidEventTorch, 'Tilt', 'Lead');
      }
    } else {
      clearTiltViewDetailsData();
    }
  }, [isOpen, tiltData, loadTiltViewDetails, clearTiltViewDetailsData]);

  // Reset selected row when data changes
  useEffect(() => {
    setSelectedRowIndex(null);
  }, [tiltViewDetails?.data?.data]);

  // Get the selected row or first row for details section
  const selectedRow = selectedRowIndex !== null ? tiltViewDetails?.data?.data[selectedRowIndex] : tiltViewDetails?.data?.data[0];

  // Keep title as DATA360 - don't change it
  // useEffect(() => {
  //   if (isOpen) {
  //     const jobNumber = weldData?.jobNumber?.[0] || selectedRow?.job_number || tiltData.bugNumber || tiltData.id || 'N/A';
  //     const passName = tiltData.pass || 'N/A';
  //     const stationName = tiltData.stationName || 'Station';
  //     const bugType = tiltData.bugType || 'N/A';
  //     const torch = tiltData.torch || 'N/A';
  //     
  //     const title = `${passName} > ${stationName} > ${bugType} > ${torch}`;
  //     document.title = title;
  //   } else {
  //     // Reset title when modal closes
  //     document.title = 'DATA360';
  //   }
  // }, [isOpen, weldData?.jobNumber, selectedRow?.job_number, tiltData]);


  // Helper function to get GPS coordinates for display
  const getGPSCoordinates = (weldData: any) => {
    if (!weldData?.gps) return '-';
    
    try {
      let gpsInfo = weldData.gps;
      if (typeof weldData.gps === 'string') {
        gpsInfo = JSON.parse(weldData.gps);
      }
      
      const lat = gpsInfo?.Latitude || gpsInfo?.latitude;
      const lng = gpsInfo?.Longitude || gpsInfo?.longitude;
      
      if (lat && lng) {
        return `${parseFloat(lat).toFixed(6)}, ${parseFloat(lng).toFixed(6)}`;
      }
    } catch (error) {
    }
    
    return '-';
  };

  // Helper function to get fixed column details
  const getFixedColumnDetails = (row: any) => {
    if (!row) return [];
    
    const details: Array<{ label: string; value: string }> = [];
    
    // Fixed columns from the table - include passView data as fallback
    details.push({ label: 'Tilt Range', value: row.tilt_range || tiltData.tiltRange || '-' });
    details.push({ label: 'Pass Name', value: row.pass || row.pass_name || tiltData.pass || '-' });
    details.push({ label: 'Status', value: row.status || row.instantaneous_status || tiltData.status || '-' });
    details.push({ label: 'Station', value: row.station_name || tiltData.stationName || '-' });
    details.push({ label: 'Bug Type', value: row.bug_type || tiltData.bugType || '-' });
    details.push({ label: 'Torch', value: row.torch || tiltData.torch || '-' });
    details.push({ label: 'Bug Number', value: getValueWithPriority(row.bug_number || tiltData.bugNumber, tiltData.passView?.bug_number) });
    details.push({ label: 'GPS (deg)', value: getGPSCoordinates(weldData) });
    details.push({ label: 'Auto Increment', value: getValueWithPriority(row.auto_increment || tiltData.autoincrement, tiltData.passView?.auto_increment) });
    details.push({ label: 'Weld Time', value: row.weld_time ? String(formatDuration(row.weld_time)) : tiltData.weldTime ? String(formatDuration(tiltData.weldTime)) : '-' });
    
    // Additional fields that might be available
    if (row.event) details.push({ label: 'Event', value: row.event });
    if (row.job_number) details.push({ label: 'Job Number', value: row.job_number });
    if (row.time) details.push({ label: 'Time', value: formatTimestamp(row.time) });
    if (row.tilt !== undefined) details.push({ label: 'Tilt', value: row.tilt.toString() });
    
    return details;
  };

  // Helper function to get parameter details with min/avg/max
  const getParameterDetails = (row: any) => {
    if (!row) return [];
    
    const details: Array<{ label: string; min: string; avg: string; max: string }> = [];
    
    // Helper function to format min/avg/max object or single value
    const formatMinAvgMax = (field: any, decimals: number = 2) => {
      if (field && typeof field === 'object' && (field.min !== undefined || field.avg !== undefined || field.max !== undefined)) {
        // Handle min/avg/max object structure
        const min = field.min !== null && field.min !== undefined ? field.min.toFixed(decimals) : '-';
        const avg = field.avg !== null && field.avg !== undefined ? field.avg.toFixed(decimals) : '-';
        const max = field.max !== null && field.max !== undefined ? field.max.toFixed(decimals) : '-';
        return { min, avg, max };
      } else if (field !== null && field !== undefined) {
        // Handle single value
        const value = typeof field === 'number' ? field.toFixed(decimals) : String(field);
        return { min: value, avg: value, max: value };
      }
      return { min: '-', avg: '-', max: '-' };
    };
    
    // Welding parameters - handle both object and single value formats
    const travelSpeed = formatMinAvgMax(row.travelSpeed || row.travel_speed, 1);
    details.push({
      label: 'Travel Speed (mm/min)',
      min: travelSpeed.min,
      avg: travelSpeed.avg,
      max: travelSpeed.max
    });
    
    const voltage = formatMinAvgMax(row.voltage || row.volts, 1);
    details.push({
      label: 'Voltage (V)',
      min: voltage.min,
      avg: voltage.avg,
      max: voltage.max
    });
    
    const current = formatMinAvgMax(row.current, 2);
    details.push({
      label: 'Current (A)',
      min: current.min,
      avg: current.avg,
      max: current.max
    });
    
    const wireSpeed = formatMinAvgMax(row.wireSpeed || row.wire_speed, 0);
    details.push({
      label: `Wire Speed (${getParameterUnitSuffix('wire_speed')})`,
      min: wireSpeed.min,
      avg: wireSpeed.avg,
      max: wireSpeed.max
    });
    
    const oscillationWidth = formatMinAvgMax(row.oscillationWidth || row.oscillation_width, 2);
    details.push({
      label: `Oscillation Width (${getParameterUnitSuffix('oscillation_width')})`,
      min: oscillationWidth.min,
      avg: oscillationWidth.avg,
      max: oscillationWidth.max
    });
    
    const heat = formatMinAvgMax(row.heat, 2);
    details.push({
      label: formatHeatInputLabel(getParameterUnitSuffix('heat')),
      min: heat.min,
      avg: heat.avg,
      max: heat.max
    });
    
    const trueEnergy = formatMinAvgMax(row.trueEnergy || row.true_energy, 2);
    details.push({
      label: `True Energy (${getParameterUnitSuffix('true_energy')})`,
      min: trueEnergy.min,
      avg: trueEnergy.avg,
      max: trueEnergy.max
    });
    
    // Additional parameters that might be available
    const target = formatMinAvgMax(row.target, 0);
    details.push({
      label: 'Target (A)',
      min: target.min,
      avg: target.avg,
      max: target.max
    });
    
    const horizontalBias = formatMinAvgMax(row.horizontalBias || row.horizontal_bias, 2);
    details.push({
      label: 'Horizontal Bias (A)',
      min: horizontalBias.min,
      avg: horizontalBias.avg,
      max: horizontalBias.max
    });
    
    const frequency = formatMinAvgMax(row.frequency, 0);
    details.push({
      label: 'Frequency (cyc/min)',
      min: frequency.min,
      avg: frequency.avg,
      max: frequency.max
    });
    
    const totalWireConsumed = formatMinAvgMax(row.totalWireConsumed || row.total_wire_consumed, 2);
    details.push({
      label: `Total Wire Consumed (${getParameterUnitSuffix('total_wire_consumed')})`,
      min: totalWireConsumed.min,
      avg: totalWireConsumed.avg,
      max: totalWireConsumed.max
    });
    
    return details;
  };



  // Format time for display (legacy function)
  const formatTime = (time: string) => {
    if (!time) return '-';
    try {
      const date = new Date(time);
      return date.toLocaleString();
    } catch (error) {
      return time;
    }
  };

  // Calculate weld time in minutes
  const calculateWeldTime = (startTime: string, endTime?: string) => {
    if (!startTime) return null;
    try {
      const start = new Date(startTime);
      const end = endTime ? new Date(endTime) : new Date();
      const diffMs = end.getTime() - start.getTime();
      return Math.round(diffMs / (1000 * 60));
    } catch (error) {
      return null;
    }
  };

  // Helper function to get value with priority (direct field -> passView -> fallback)
  const getValueWithPriority = (directValue: any, passViewValue: any, fallback: string = '-') => {
    if (directValue !== null && directValue !== undefined && directValue !== '') {
      return directValue;
    }
    if (passViewValue !== null && passViewValue !== undefined && passViewValue !== '') {
      return passViewValue;
    }
    return fallback;
  };

  // Get dynamic unit labels for basic details
  const getUnitLabel = (parameter: string): string => {
    return getParameterUnitSuffix(parameter);
  };

  // Convert diameter values from inches to display unit
  const convertDiameterValue = (value: any): string => {
    if (value === undefined || value === null || value === '') return '-';
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numericValue)) return value;
    const convertedValue = convertWeldDataValue(numericValue);
    return convertedValue.toFixed(2);
  };

  // Summary details - prioritize tiltData object, then passView data, then API data
  const details = [
    { label: 'Job Number', value: selectedRow?.job_number || weldData?.jobNumber?.[0] || tiltData.bugNumber || tiltData.id || '-' },
    { label: 'Station', value: tiltData.stationName || tiltData.passView?.station_name || selectedRow?.station_name || '-' },
    { label: 'Bug Type', value: tiltData.bugType || tiltData.passView?.bug_type || selectedRow?.bug_type || '-' },
    { label: 'Torch', value: tiltData.torch || tiltData.passView?.torch || selectedRow?.torch || '-' },
    { label: 'Tilt Range', value: tiltData.tiltRange || selectedRow?.tilt_range || '-' },
    { label: 'Bug Number', value: getValueWithPriority(tiltData.bugNumber, tiltData.passView?.bug_number) },
    { label: `Band Diameter (${getUnitLabel('band_diameter')})`, value: convertDiameterValue(getValueWithPriority(tiltData.bandDiameter, tiltData.passView?.band_diameter)) },
    { label: `Logging Distance (${getUnitLabel('logging_distance')})`, value: convertDiameterValue(getValueWithPriority(tiltData.loggingDistance, tiltData.passView?.logging_distance)) },
    { label: `Pipe Diameter (${getUnitLabel('pipe_diameter')})`, value: convertDiameterValue(getValueWithPriority(tiltData.pipeDiameter, tiltData.passView?.pipe_diameter)) },
    { label: 'Weld Start Time', value: tiltData.weldStartTime ? formatDateTime(tiltData.weldStartTime) : '-' },
    { label: 'Weld End Time', value: tiltData.weldEndTime ? formatDateTime(tiltData.weldEndTime * 1000) : '-' },
    { label: 'GPS (deg)', value: getGPSCoordinates(weldData) },
    { label: 'Auto Increment', value: getValueWithPriority(tiltData.autoincrement, tiltData.passView?.auto_increment) },
    { label: 'Weld Time', value: tiltData.weldTime ? formatDuration(tiltData.weldTime) : '-' },
  ];

  // Color palette for badges (similar to index.tsx)
  const colorPalette = [
    'bg-green-200', 'bg-blue-100', 'bg-green-100', 'bg-red-100', 
    'bg-cyan-100', 'bg-teal-100', 'bg-indigo-100', 'bg-lime-100',
    'bg-pink-100', 'bg-rose-100', 'bg-emerald-100', 'bg-violet-100',
    'bg-amber-100', 'bg-sky-100', 'bg-stone-100', 'bg-slate-100',
    'bg-zinc-100', 'bg-purple-100', 'bg-orange-100', 'bg-yellow-100'
  ];

  // Convert details to infoItems with colors
  const infoItems = details.map((item, index) => ({
    ...item,
    bgColor: colorPalette[index % colorPalette.length]
  }));

  const visibleItems = showAllInfo ? infoItems : infoItems.slice(0, 7);

  // Handle update modal
  const handleOpenUpdateModal = (weld: any) => {
    setWeldToUpdate(weld);
    setUpdateModalOpen(true);
  };

  const handleCloseUpdateModal = () => {
    if (!updateWeldNumberLoading) {
      setUpdateModalOpen(false);
      setWeldToUpdate(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0 overflow-hidden">
        <div className="flex flex-col h-full font-sans overflow-hidden">
          {/* Header */}
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between bg-white flex-shrink-0">
            <div className="flex items-center gap-4">
              <Button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                variant="outline"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold">{weldData?.weldNumber || tiltData.bugNumber || tiltData.id || '-'}</h1>
                <div className="text-xs sm:text-sm text-gray-600 mt-1 space-y-1">
                  <div className="flex flex-wrap gap-2">
                    {visibleItems.map((item, index) => (
                      <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs`}>
                        {item.label}: {item.value}
                      </span>
                    ))}
                    {infoItems.length > 7 && (
                      <button
                        onClick={() => setShowAllInfo(!showAllInfo)}
                        className="bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                      >
                        {showAllInfo ? 'Show Less' : `Show More (+${infoItems.length - 7})`}
                        <span className="text-xs">{showAllInfo ? '▲' : '▼'}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Show loader while data is loading */}
            {tiltViewDetailsLoading ? (
              <div className="flex-1 flex items-center justify-center bg-white">
                <div className="flex flex-col items-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB1C37]"></div>
                  <p className="text-gray-600 text-lg font-medium">Loading tilt view details...</p>
                  <p className="text-gray-400 text-sm">Please wait while we fetch the data</p>
                </div>
              </div>
            ) : tiltViewDetailsError ? (
              <div className="flex-1 flex items-center justify-center bg-white">
                <div className="flex flex-col items-center space-y-4 max-w-md text-center">
                  <div className="text-red-500 text-5xl">⚠️</div>
                  <p className="text-red-600 text-lg font-medium">Error loading tilt view details</p>
                  <p className="text-gray-500 text-sm">{tiltViewDetailsError}</p>
                  <Button onClick={onClose} variant="outline">Close</Button>
                </div>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
                <div className="sticky top-0 bg-white rounded-t-lg z-10 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center justify-between px-1 md:px-4 py-2">
                    <TabsList className="h-auto flex-1 justify-start gap-2 md:gap-4 lg:gap-8 border-b bg-transparent py-2 flex-wrap">
                      <TabsTrigger value="table" className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap">
                        <List className="inline-block mr-1 h-4 w-4 align-text-bottom" /> Data Analysis
                      </TabsTrigger>
                      <TabsTrigger value="graph" className="data-[state=active]:border-[#EB1C37] data-[state=active]:text-[#EB1C37] relative px-1 pb-3 pt-2 text-sm md:text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-[0.5px] whitespace-nowrap">
                        <TrendingUp className="inline-block mr-1 h-4 w-4 align-text-bottom" /> Graph View
                      </TabsTrigger>
                    </TabsList>
                    {activeTab === 'table' && (
                      <div className="flex gap-2 px-2">
                        <Button
                          onClick={() => setIsColumnModalOpen(true)}
                          className="flex items-center gap-2"
                          title="Configure columns"
                        >
                          <Columns className="h-4 w-4" />
                          <span className="hidden sm:inline">Columns</span>
                        </Button>
                        {canEdit && (
                          <Button
                            title="Edit Weld Number"
                            onClick={() => handleOpenUpdateModal(weldData)}
                            disabled={!weldData}
                          >
                            <Pencil className="h-3 w-3" /> Edit Weld Number
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <TabsContent value="table" className="flex-1 min-h-0 overflow-hidden">
                  <div className="h-full px-4">
                    <TiltViewDetailTable 
                      detailData={tiltViewDetails?.data?.data || []}
                      onRowSelect={setSelectedRowIndex}
                      selectedRowIndex={selectedRowIndex}
                      loading={tiltViewDetailsLoading}
                      tiltStart={tiltData?.tilt_start}
                      tiltEnd={tiltData?.tilt_end}
                      statusCalculationMethod={statusCalculationMethod}
                      visibleColumns={visibleColumns}
                    />
                  </div>
                </TabsContent>
                <TabsContent value="graph" className="flex-1 min-h-0 overflow-hidden">
                  <div className="h-full overflow-y-auto">
                    <div className="p-4 pb-20">
                      <PassViewGraph
                        detailData={tiltViewDetails?.data?.data || []}
                        projectNumber={getValueWithPriority(tiltData.bugNumber, tiltData.passView?.bug_number) || ''}
                        projectName={tiltData.tiltRange || ''}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>

          {/* Weld Number Update Modal */}
          <UpdateWeldNumberDialog
            open={updateModalOpen}
            onOpenChange={(open) => {
              if (!open) {
                handleCloseUpdateModal();
              }
            }}
            title="Update Weld Number"
            currentValue={weldToUpdate?.weldNumber || ''}
            showJobNumber={false}
            currentJobNumber={
              tiltData.jobNumber ||
              selectedRow?.job_number ||
              ''
            }
            auditContext={{
              projectId: currentProject?.project?.projectId,
              projectName: currentProject?.project?.projectName,
              productionWeldId: weldToUpdate?.id,
            }}
            onConfirm={(newValue) => {
              if (weldToUpdate && newValue && !updateWeldNumberLoading) {
                const currentProjectId = currentProject?.project?.projectId;
                if (currentProjectId) {
                  const selectedViewJobNumber =
                    tiltData.jobNumber ||
                    selectedRow?.job_number ||
                    '';
                  const payload = {
                    project_id: currentProjectId,
                    old_weld_number: weldToUpdate.weldNumber,
                    new_weld_number: newValue,
                    isNewWeld: false,
                    weld_uid: tiltData.welduid_s_event_torch,
                    oldJobNumber: selectedViewJobNumber,
                    newJobNumber: selectedViewJobNumber,
                  };
                  dispatch(updateWeldNumber(payload));
                }
              }
            }}
            loading={updateWeldNumberLoading}
            maxWidth="3xl"
          />
        </div>
      </DialogContent>

      {/* Column Selector Modal */}
      <ColumnSelectorModal
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        onSave={handleColumnsSave}
        columns={TILT_VIEW_DETAIL_COLUMNS}
        selectedColumns={visibleColumns}
        title="Select Columns - Tilt View Detail"
        defaultColumns={defaultColumnsFromAPI || getAllColumnKeys(TILT_VIEW_DETAIL_COLUMNS)}
      />
    </Dialog>
  );
};

export default TiltViewDetailModal; 